import { useEffect, useRef } from "react";
import { INACTIVITY_LOGOUT_TIME, STORAGE_KEY_LAST_ACTIVITY } from "../constants";

/**
 * Logs out the user after a period of inactivity.
 * Production 2 hours, testing 2 minutes.
 * To test change constant in constants file.
 *
 * @param logout - Invoke when user should be logged out.
 * @param timeoutMs - Inactivity timeout in milliseconds.
 *
 * For quick testing: set timeoutMs = 2 * 60 * 1000 (2 minutes)
 * For production: set timeoutMs = 2 * 60 * 60 * 1000 (2 hours)
 */
export function useInactivityLogout(logout: () => void, timeoutMs = INACTIVITY_LOGOUT_TIME) {
	const timerRef = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		const resetTimer = () => {
			if (timerRef.current) clearTimeout(timerRef.current);

			// Save last activity timestamp in localStorage so it's shared across tabs.
			localStorage.setItem(STORAGE_KEY_LAST_ACTIVITY, Date.now().toString());

			timerRef.current = setTimeout(() => {
				logout();
			}, timeoutMs);
		};

		// Check on load if last activity was too long ago.
		const lastActivity = parseInt(localStorage.getItem(STORAGE_KEY_LAST_ACTIVITY) || "0", 10);
		if (lastActivity && Date.now() - lastActivity > timeoutMs) {
			logout();
			return;
		}

		// When activity occurs in another tab, update all tabs.
		const handleStorage = (event: StorageEvent) => {
			if (event.key === STORAGE_KEY_LAST_ACTIVITY) {
				const last = parseInt(event.newValue || "0", 10);
				if (Date.now() - last > timeoutMs) {
					logout();
				} else {
					resetTimer();
				}
			}
		};

		// Events that count as "activity".
		const activityEvents = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];

		// Add event listeners
		activityEvents.forEach((event) => window.addEventListener(event, resetTimer));
		window.addEventListener("storage", handleStorage);

		// Initialize.
		resetTimer();

		// Cleanup on unmount.
		return () => {
			if (timerRef.current) clearTimeout(timerRef.current);
			activityEvents.forEach((event) => window.removeEventListener(event, resetTimer));
			window.removeEventListener("storage", handleStorage);
		};
	}, [logout, timeoutMs]);
}
